/*
  fp-ai-generator.js
  Upload -> modal -> generatie (live status) -> watermerk-preview + canvas-mockup.
  In de modal schuiven de PDP-koopblokken (variant-picker, prijs, ATC, upsell);
  die worden teruggezet bij sluiten. De <dialog> blijft een kind van
  <product-info>, zodat Dawn's variant-JS de nodes blijft vinden.
*/
if (!customElements.get('fp-ai-generator')) {
  customElements.define(
    'fp-ai-generator',
    class FpAiGenerator extends HTMLElement {
      connectedCallback() {
        this.endpoint = this.dataset.endpoint;
        this.sceneId = this.dataset.sceneId;
        this.generationId = null;
        this.previewUrl = null;
        this.sourceDataUrl = null;
        this.remaining = null;
        this.relocated = [];
        this.busyTimer = null;
        this.controller = null;
        this.inFlight = false;

        this.productInfo = this.closest('product-info') || document;
        this.modal = this.querySelector('.fp-ai-modal');
        this.busyEl = this.querySelector('[data-role="busy"]');
        this.resultEl = this.querySelector('[data-role="result"]');
        this.statusEl = this.querySelector('.fp-ai-gen__status');
        this.busyText = this.querySelector('.fp-ai-gen__busy-text');
        this.buyPanel = this.querySelector('[data-role="buy-panel"]');
        this.redoBtn = this.querySelector('.fp-ai-gen__redo');
        this.previewTargets = this.querySelectorAll('.fp-ai-gen__preview');

        this.busyMessages = [
          'Uploading the photo…',
          'Studying the face…',
          'Painting the scene…',
          'Adding magical details…',
          'Almost there — final touches…',
        ];

        this.triggerEl = this.querySelector('[data-role="trigger"]');
        this.doneEl = this.querySelector('[data-role="done"]');
        this.doneThumb = this.querySelector('.fp-ai-gen__done-thumb');
        this.triesEl = this.querySelector('.fp-ai-gen__tries');
        this.lightbox = this.querySelector('.fp-ai-gen__lightbox');
        this.querySelector('.fp-ai-gen__zoom').addEventListener('click', () => this.openLightbox());
        this.lightbox.addEventListener('click', () => { this.lightbox.hidden = true; });
        this.querySelector('.fp-ai-gen__done-open').addEventListener('click', () => {
          this.openModal();
          this.setStage('result');
        });

        // Testmodus voor admin: ?fp_ai_test=1 hergebruikt een vaste generatie
        // (geen AI-tokens of Cloudinary-opslag tijdens het testen van de UI).
        const params = new URLSearchParams(window.location.search);
        if (params.get('fp_ai_test') && this.dataset.testPreview) {
          this.applyResult({
            generationId: this.dataset.testGenerationId || 'test',
            previewUrl: this.dataset.testPreview,
            remaining: null,
          });
        } else {
          this.restoreSession();
        }

        this.querySelector('.fp-ai-gen__input').addEventListener('change', (e) => {
          const file = e.target.files && e.target.files[0];
          if (file) this.handleFile(file);
        });
        this.redoBtn.addEventListener('click', () => this.sourceDataUrl && this.generate());
        this.querySelector('.fp-ai-gen__restart').addEventListener('click', () => this.restart());
        this.querySelector('.fp-ai-modal__close').addEventListener('click', () => this.closeModal());
        this.modal.addEventListener('cancel', () => this.onModalClosed());
        this.modal.addEventListener('close', () => this.onModalClosed());
        // Klik op de achtergrond (buiten de modal-inhoud) sluit ook.
        this.modal.addEventListener('click', (e) => {
          if (e.target === this.modal) this.closeModal();
        });

        this.querySelectorAll('[data-view-btn]').forEach((btn) => {
          btn.addEventListener('click', () => this.switchView(btn.dataset.viewBtn));
        });

        // Submit-gate: blokkeer ATC tot er een generatie is; injecteer properties.
        this.onSubmit = (event) => {
          const form = event.target;
          // getAttribute i.p.v. form.id: Shopify-formulieren hebben <input name="id">
          // (de variant-id) en die overschaduwt de id-property van het formulier.
          if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== this.dataset.productFormId) return;
          if (!this.generationId) {
            event.preventDefault();
            event.stopPropagation();
            this.showStatus(this.dataset.textGate);
            return;
          }
          this.ensureProperty(form, 'properties[_generation_id]', this.generationId);
          this.ensureProperty(form, 'properties[Preview]', this.previewUrl);
          // Direct sluiten: Dawn's eigen handler (op het formulier zelf) draait
          // hierna en opent de drawer; die moet vrij zicht krijgen.
          this.closeModal();
        };
        document.addEventListener('submit', this.onSubmit, true);
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmit, true);
      }

      ensureProperty(form, name, value) {
        let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.appendChild(input);
        }
        input.value = value;
      }

      /* ---------- modal + relocatie ---------- */

      openModal() {
        if (!this.modal.open) {
          this.relocateBuyBlocks();
          this.modal.showModal();
        }
      }

      closeModal() {
        if (this.modal.open) this.modal.close();
      }

      onModalClosed() {
        // Genereren onderbreken als de klant wegklikt: verzoek annuleren, geen
        // half-af generationId laten staan (anders kon je bestellen zonder preview).
        if (this.inFlight && this.controller) {
          this.controller.abort();
        }
        this.stopBusyMessages();
        this.restoreBuyBlocks();
        // Klaar resultaat blijft beschikbaar op de PDP als "preview ready"-rij.
        this.updateTrigger();
      }

      updateTrigger() {
        const has = !!this.generationId;
        this.triggerEl.hidden = has;
        this.doneEl.hidden = !has;
        if (has) this.doneThumb.src = this.previewUrl;
      }

      /* ---------- sessie-persistentie ---------- */

      sessionKey() {
        return `fpAiGen:${this.sceneId}`;
      }

      saveSession() {
        try {
          sessionStorage.setItem(
            this.sessionKey(),
            JSON.stringify({ generationId: this.generationId, previewUrl: this.previewUrl, remaining: this.remaining })
          );
        } catch (e) { /* private mode */ }
      }

      restoreSession() {
        try {
          const raw = sessionStorage.getItem(this.sessionKey());
          if (!raw) return;
          const data = JSON.parse(raw);
          if (data && data.generationId && data.previewUrl) this.applyResult(data);
        } catch (e) { /* ignore */ }
      }

      clearSession() {
        try { sessionStorage.removeItem(this.sessionKey()); } catch (e) { /* ignore */ }
      }

      // Zet een (herstelde of verse) generatie in de UI.
      applyResult(data) {
        this.generationId = data.generationId;
        this.previewUrl = data.previewUrl;
        this.remaining = typeof data.remaining === 'number' ? data.remaining : null;
        this.previewTargets.forEach((img) => { img.src = data.previewUrl; });
        this.setMockupImages(data.previewUrl);
        this.redoBtn.disabled = this.remaining !== null && this.remaining <= 0;
        if (this.triesEl) {
          const show = this.remaining !== null && this.remaining >= 0 && this.dataset.textTries;
          this.triesEl.hidden = !show;
          if (show) this.triesEl.textContent = this.dataset.textTries.replace('{count}', this.remaining);
        }
        this.switchView('flat');
        this.setStage('result');
        this.updateTrigger();
        this.updateGallery();
        // Properties meteen in het formulier: dynamic checkout-knoppen (Buy now,
        // Shop Pay, PayPal) serialiseren het formulier zonder submit-event.
        this.injectProperties();
        if (this.productInfo.classList) this.productInfo.classList.add('fp-ai-ready');
      }

      injectProperties() {
        const form = document.getElementById(this.dataset.productFormId);
        if (!form || !this.generationId) return;
        this.ensureProperty(form, 'properties[_generation_id]', this.generationId);
        this.ensureProperty(form, 'properties[Preview]', this.previewUrl);
      }

      removeProperties() {
        const form = document.getElementById(this.dataset.productFormId);
        if (!form) return;
        ['properties[_generation_id]', 'properties[Preview]'].forEach((name) => {
          form.querySelector(`input[name="${CSS.escape(name)}"]`)?.remove();
        });
      }

      /* ---------- Cloudinary canvas-mockups ---------- */

      // Monteert de preview met een multiply-blend op een foto van een lege
      // canvas, zodat doektextuur en lichtval erdoorheen schijnen. Base-config:
      // "publicId|x|y|w|h" (het witte canvasvlak in de basisfoto). Afgeleiden
      // worden door Cloudinary gecachet: geen opslag- of AI-kosten.
      buildMockupUrl(previewUrl, baseConfig) {
        if (!baseConfig) return null;
        const [baseId, x, y, w, h] = baseConfig.split('|');
        if (!baseId || !w) return null;
        // Preview-URL's kunnen een q_auto/f_auto-segment bevatten; dat hoort niet
        // in het overlay-id, dus de regex slaat het over.
        const m = previewUrl.match(/^(https:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload)(?:\/(?:f_auto|q_auto)[^/]*)?\/(?:v\d+\/)?(.+?)\.\w+$/);
        if (!m) return null;
        const overlayId = m[2].replace(/\//g, ':');
        return `${m[1]}/l_${overlayId},w_${w},h_${h},c_fill/fl_layer_apply,e_multiply,g_north_west,x_${x},y_${y}/w_1100,q_auto:eco/${baseId}.webp`;
      }

      setMockupImages(previewUrl) {
        const targets = [
          { img: '.fp-ai-gen__canvas-img', tab: '[data-view-btn="canvas"]', base: this.dataset.canvasBase },
          { img: '.fp-ai-gen__room-img', tab: '[data-view-btn="room"]', base: this.dataset.roomBase },
        ];
        targets.forEach(({ img, tab, base }) => {
          const url = this.buildMockupUrl(previewUrl, base);
          const el = this.querySelector(img);
          const btn = this.querySelector(tab);
          if (url) {
            el.src = url;
            btn.hidden = false;
          } else {
            btn.hidden = true;
          }
        });
      }

      relocateBuyBlocks() {
        if (this.relocated.length) return;
        const selectors = [
          // De wrapper (niet het inner .price-element): Dawn's variant-JS
          // vervangt de innerHTML van deze wrapper bij het wisselen van maat.
          `#price-${this.dataset.sectionId}`,
          `#variant-selects-${this.dataset.sectionId}`,
          `#variant-radios-${this.dataset.sectionId}`,
          'variant-selects',
          // Dawn 11 (dit thema) rendert de knoppen-variant als <variant-radios>
          // i.p.v. <variant-selects>; Dawn 15 gebruikt altijd <variant-selects>.
          'variant-radios',
          'product-form',
          'fp-addon',
        ];
        const seen = new Set();
        selectors.forEach((sel) => {
          this.productInfo.querySelectorAll(sel).forEach((node) => {
            if (seen.has(node) || this.contains(node)) return;
            seen.add(node);
            const placeholder = document.createComment('fp-ai-relocated');
            node.parentNode.insertBefore(placeholder, node);
            this.relocated.push({ node, placeholder });
            this.buyPanel.appendChild(node);
          });
        });
      }

      restoreBuyBlocks() {
        this.relocated.forEach(({ node, placeholder }) => {
          if (placeholder.parentNode) {
            placeholder.parentNode.replaceChild(node, placeholder);
          }
        });
        this.relocated = [];
      }

      openLightbox() {
        const active = this.querySelector('.fp-ai-gen__view:not([hidden]) img');
        if (!active || !active.src) return;
        this.lightbox.querySelector('img').src = active.src;
        this.lightbox.hidden = false;
      }

      /* ---------- PDP-galerij overnemen ---------- */

      // Vervangt de eerste galerij-afbeelding door de generatie (origineel
      // wordt bewaard en teruggezet bij "use a different photo").
      updateGallery() {
        if (!this.previewUrl) return;
        const img = this.productInfo.querySelector('.product__media img');
        if (!img) return;
        if (!img.dataset.fpOriginalSrc) {
          img.dataset.fpOriginalSrc = img.src;
          img.dataset.fpOriginalSrcset = img.srcset || '';
        }
        img.srcset = '';
        img.sizes = '';
        img.src = this.previewUrl;
      }

      restoreGallery() {
        const img = this.productInfo.querySelector('.product__media img');
        if (img && img.dataset.fpOriginalSrc) {
          img.src = img.dataset.fpOriginalSrc;
          img.srcset = img.dataset.fpOriginalSrcset;
          delete img.dataset.fpOriginalSrc;
          delete img.dataset.fpOriginalSrcset;
        }
      }

      /* ---------- views ---------- */

      switchView(view) {
        this.querySelectorAll('.fp-ai-gen__view').forEach((v) => {
          v.hidden = v.dataset.view !== view;
        });
        this.querySelectorAll('[data-view-btn]').forEach((b) => {
          b.classList.toggle('is-active', b.dataset.viewBtn === view);
        });
      }

      /* ---------- upload + generatie ---------- */

      restart() {
        this.generationId = null;
        this.previewUrl = null;
        this.sourceDataUrl = null;
        this.querySelector('.fp-ai-gen__input').value = '';
        this.showStatus('');
        this.clearSession();
        this.removeProperties();
        this.restoreGallery();
        if (this.productInfo.classList) this.productInfo.classList.remove('fp-ai-ready');
        this.closeModal();
        this.updateTrigger();
      }

      async handleFile(file) {
        this.showStatus('');
        this.openModal();
        try {
          this.sourceDataUrl = await this.downscale(file);
        } catch (err) {
          this.showStatus(this.dataset.textError);
          return;
        }
        this.generate();
      }

      async downscale(file, maxSide = 1024) {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close();
        return canvas.toDataURL('image/jpeg', 0.85);
      }

      startBusyMessages() {
        let i = 0;
        this.busyText.textContent = this.busyMessages[0];
        clearInterval(this.busyTimer);
        this.busyTimer = setInterval(() => {
          i = Math.min(i + 1, this.busyMessages.length - 1);
          this.busyText.textContent = this.busyMessages[i];
        }, 5000);
      }

      stopBusyMessages() {
        clearInterval(this.busyTimer);
        this.busyTimer = null;
      }

      setStage(stage) {
        this.busyEl.hidden = stage !== 'busy';
        this.resultEl.hidden = stage !== 'result';
      }

      showStatus(message) {
        this.statusEl.textContent = message;
        this.statusEl.hidden = !message;
      }

      async generate() {
        this.setStage('busy');
        this.showStatus('');
        this.startBusyMessages();

        this.controller = new AbortController();
        this.inFlight = true;
        const timeout = setTimeout(() => this.controller.abort(), 90000);

        try {
          const response = await fetch(this.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sceneId: this.sceneId, image: this.sourceDataUrl }),
            signal: this.controller.signal,
          });
          const data = await response.json().catch(() => ({}));

          if (!response.ok) {
            this.showStatus(response.status === 429 ? this.dataset.textLimit : data.error || this.dataset.textError);
            this.setStage(this.generationId ? 'result' : 'busy');
            this.busyEl.hidden = !!this.generationId;
            return;
          }

          this.applyResult(data);
          this.saveSession();
        } catch (err) {
          // Door wegklikken geannuleerd: stil resetten, geen foutmelding.
          if (err && err.name === 'AbortError') {
            if (!this.generationId) this.setStage('busy');
            return;
          }
          this.showStatus(this.dataset.textError);
          this.setStage('busy');
          this.busyEl.hidden = false;
        } finally {
          clearTimeout(timeout);
          this.inFlight = false;
          this.stopBusyMessages();
        }
      }
    }
  );
}
